import { useInfiniteQuery } from '@tanstack/react-query';
import { CircleAlert, MapPin, Wifi } from 'lucide-react';
import { formatDate, formatPercent, formatTemperature, formatTime, type ChangeEntry } from '@ste/shared';
import { apiFetch, useAuth } from '../../api/auth.ts';
import { Button, Card, EmptyState, Spinner } from '../../components/ui.tsx';
import { t, type MessageKey } from '../../i18n/index.ts';
import { CommandError } from '../charging/CommandError.tsx';

// "Who changed what and when", also changes by STE from afar (ADR-0024)

const PAGE = 30;

export function ChangeLogCard() {
  const me = useAuth((s) => s.user?.email);
  const changes = useInfiniteQuery({
    queryKey: ['changes'],
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) =>
      apiFetch<ChangeEntry[]>(`/changes?limit=${PAGE}${pageParam ? `&before=${pageParam}` : ''}`),
    getNextPageParam: (last) => (last.length === PAGE ? last.at(-1)?.id : undefined),
  });
  const entries = changes.data?.pages.flat() ?? [];

  return (
    <Card title={t('changes.title')}>
      <p className="mb-4 text-sm text-muted">{t('changes.intro')}</p>
      {changes.isLoading && <Spinner label={t('common.loading')} />}
      {changes.isError && <CommandError error={changes.error} />}
      {changes.isSuccess && entries.length === 0 && <EmptyState>{t('changes.none')}</EmptyState>}
      <ul className="divide-y divide-border">
        {entries.map((entry) => (
          <ChangeRow key={entry.id} entry={entry} me={me} />
        ))}
      </ul>
      {changes.hasNextPage && (
        <Button
          variant="ghost"
          className="mt-3"
          disabled={changes.isFetchingNextPage}
          onClick={() => void changes.fetchNextPage()}
        >
          {changes.isFetchingNextPage ? t('common.loading') : t('changes.more')}
        </Button>
      )}
    </Card>
  );
}

function ChangeRow({ entry, me }: { entry: ChangeEntry; me: string | undefined }) {
  const at = new Date(entry.timestamp);
  const who = entry.email === me ? t('changes.you') : entry.email;
  // unknown keys give undefined at runtime: fall back to the raw value
  const role = t(`changes.role.${entry.role}` as MessageKey) || entry.role;
  const detail = describe(entry);
  return (
    <li className="py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium">
            {label(entry)}
            {!entry.ok && (
              <span className="ml-2 inline-flex items-center gap-1 text-sm text-warm-text">
                <CircleAlert size={14} aria-hidden /> {t('changes.failed')}
              </span>
            )}
          </div>
          {detail && <div className="text-sm text-muted">{detail}</div>}
          <div className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-muted">
            <span className="break-all">{who}</span>
            <span>· {role}</span>
            <span className="inline-flex items-center gap-1">
              {entry.source === 'cloud' ? <Wifi size={14} aria-hidden /> : <MapPin size={14} aria-hidden />}
              {t(entry.source === 'cloud' ? 'changes.remote' : 'changes.local')}
            </span>
          </div>
        </div>
        <time className="shrink-0 text-right text-sm text-muted" dateTime={entry.timestamp}>
          {formatDate(at)}
          <br />
          {formatTime(at)}
        </time>
      </div>
    </li>
  );
}

function label(entry: ChangeEntry): string {
  return t(`changes.action.${entry.action}` as MessageKey) || entry.action;
}

/** one line of harmless details, e.g. the comment of an activation or the new charge mode */
function describe(entry: ChangeEntry): string | undefined {
  const d = entry.details;
  const loadpoint = entry.target?.startsWith('loadpoint:')
    ? t('changes.loadpoint', { n: entry.target.slice('loadpoint:'.length) })
    : undefined;
  const parts: string[] = [];
  if (loadpoint) parts.push(loadpoint);
  if (typeof d.comment === 'string') parts.push(`„${d.comment}“`);
  if (typeof d.mode === 'string') {
    parts.push(t(`mode.${d.mode}` as MessageKey) || d.mode);
  }
  if (typeof d.soc === 'number') parts.push(formatPercent(d.soc));
  if (typeof d.temperature === 'number') parts.push(formatTemperature(d.temperature));
  if (typeof d.ssid === 'string') parts.push(d.ssid);
  if (typeof d.prioritySoc === 'number')
    parts.push(`${t('battery.prioritySoc')} ${formatPercent(d.prioritySoc)}`);
  if (typeof d.bufferSoc === 'number') parts.push(`${t('battery.bufferSoc')} ${formatPercent(d.bufferSoc)}`);
  if (typeof d.from === 'string') parts.push(t('changes.fromBackup', { serial: d.from }));
  if (entry.target === 'evcc' || entry.target === 'gateway') parts.push(t(`changes.target.${entry.target}`));
  return parts.length > 0 ? parts.join(' · ') : undefined;
}
